/**
 * Users Service
 *
 * Service for managing organization users.
 * Uses ApiResponse<T> pattern - response.data is already T.
 *
 * TODO(client): Migrate all endpoints to typed client when generated.
 * Currently using apiClient.get/post() as temporary fallback.
 */

import { apiClient } from "@/services/shared/infrastructure";
import { makeService } from "@/services/shared/infrastructure/service-wrapper";
import { getEffectiveOrgId } from "@/services/shared/infrastructure/tenant-state";
import type {
  IdentityProfile,
  OrganizationUser,
  UpdateIdentityProfileInput,
  UsersListResponse,
} from "./identity.view-models";

// ============================================================================
// Response Type Definitions (for API responses)
// ============================================================================

type UsersListApiResponse = {
  items: OrganizationUser[];
  meta?: { pagination?: { nextCursor?: string } };
};

const rawUsersService = {
  /**
   * List users in organization
   */
  async listUsers(
    params?: { role?: string; cursor?: string; limit?: number },
    signal?: AbortSignal,
  ): Promise<UsersListResponse> {
    const orgId = getEffectiveOrgId();
    if (!orgId) {
      throw new Error("Organization ID is required");
    }
    try {
      // TODO(client): migrate when generated
      // const response = await api.identity.users.list({ orgId, ...params });

      // T = UsersListApiResponse, so response.data.items is OrganizationUser[]
      const response = await apiClient.get<UsersListApiResponse>(
        `/orgs/${orgId}/identity/users`,
        { params, signal },
      );

      // response.data is already UsersListApiResponse
      const items = response.data.items || [];
      const nextCursor =
        response.data.meta?.pagination?.nextCursor ||
        response.meta?.pagination?.nextCursor ||
        undefined;

      return {
        items,
        nextCursor,
        totalCount: items.length, // Backend should provide this in meta
      };
    } catch (error: any) {
      // Re-throw AbortErrors - React Query handles them silently
      if (
        (error instanceof DOMException && error.name === "AbortError") ||
        error?.name === "AbortError"
      ) {
        throw error;
      }

      return { items: [] };
    }
  },

  /**
   * Get a single user by id
   */
  async getUser(id: string, signal?: AbortSignal): Promise<OrganizationUser> {
    const orgId = getEffectiveOrgId();
    if (!orgId) {
      throw new Error("Organization ID is required");
    }
    const response = await apiClient.get<OrganizationUser>(
      `/orgs/${orgId}/identity/users/${id}`,
      { signal },
    );
    return response.data;
  },

  /**
   * Get the authenticated user's self-profile (freshly presigned media URLs).
   */
  async getCurrentProfile(signal?: AbortSignal): Promise<IdentityProfile> {
    const orgId = getEffectiveOrgId();
    if (!orgId) {
      throw new Error("Organization ID is required");
    }
    const response = await apiClient.get<IdentityProfile>(
      `/orgs/${orgId}/identity/profile`,
      { signal },
    );
    return response.data;
  },

  /**
   * Patch safe self-service profile fields for the authenticated user.
   */
  async updateCurrentProfile(
    input: UpdateIdentityProfileInput,
  ): Promise<IdentityProfile> {
    const orgId = getEffectiveOrgId();
    if (!orgId) {
      throw new Error("Organization ID is required");
    }
    const response = await apiClient.patch<IdentityProfile>(
      `/orgs/${orgId}/identity/profile`,
      input,
    );
    return response.data;
  },

  /**
   * Upload avatar or cover for the authenticated user.
   */
  async uploadCurrentProfileMedia(
    kind: "avatar" | "cover",
    file: File,
  ): Promise<IdentityProfile> {
    const orgId = getEffectiveOrgId();
    if (!orgId) {
      throw new Error("Organization ID is required");
    }
    const formData = new FormData();
    formData.append("file", file);
    const response = await apiClient.post<IdentityProfile>(
      `/orgs/${orgId}/identity/profile/media/${kind}`,
      undefined,
      { body: formData, timeout: 120000 },
    );
    return response.data;
  },

  /**
   * Remove avatar or cover for the authenticated user.
   */
  async deleteCurrentProfileMedia(
    kind: "avatar" | "cover",
  ): Promise<IdentityProfile> {
    const orgId = getEffectiveOrgId();
    if (!orgId) {
      throw new Error("Organization ID is required");
    }
    const response = await apiClient.delete<IdentityProfile>(
      `/orgs/${orgId}/identity/profile/media/${kind}`,
    );
    return response.data;
  },
};

export const usersService = makeService(rawUsersService, "users");
